/* eslint-disable react-hooks/exhaustive-deps */
import {BaseDepartmentId} from "types/departments"
import {useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState} from "react"
import {CommunicationContext} from "context/CommunicationContext"
import {Communication} from "types/communication"
import {getFullName, getUnreadCountBadge, handleError, toastInfo, toastSuccess} from "helpers"
import {useQuery} from "@tanstack/react-query"
import {useModel} from "hooks"
import {intersection} from "lodash"
import {chatServiceV2} from "services"
import {Tabs} from "antd"
import {TabHeader} from "components/Tab"
import {DirectChat} from "sections/NewCommunication/parts/DirectChat"
import {BaseLoading} from "components"
import styles from "./StudentChat.module.css"
import cx from "classnames"
import {Compose} from "../Compose"
import {convertRoom} from "helpers/chat"

type Props = {
    user: any
    departmentId?: BaseDepartmentId
    userIdField: string
    isSidebarChat?: boolean
}

const STUDENT_CHAT_KEYS = {
    DIRECT: "direct",
    DEPARTMENT: "department"
}

export function StudentChat(props: Props) {
    const {user, departmentId, userIdField, isSidebarChat = false} = props
    const {changeRoom, activeRoom, changeViewAs} = useContext(CommunicationContext)
    const model = useModel()
    const [isViewAsCleared, setIsViewAsCleared] = useState(false)
    const enabledDirectMessages = model.chatOptions[Communication.ChatOptions.EnabledDirectMessages]
    const chatSettings = model.chatOptions[Communication.ChatOptions.Settings] as Communication.ChatSettings
    const [activeKey, setActiveKey] = useState(
        chatSettings?.defaultChatOnModule === Communication.DefaultChatOnModule.Departmental
            ? STUDENT_CHAT_KEYS.DEPARTMENT
            : STUDENT_CHAT_KEYS.DIRECT
    )
    const userId = user?.[userIdField]
    const [isLoading, setIsLoading] = useState(false)

    useLayoutEffect(() => {
        changeViewAs(undefined)
        setIsViewAsCleared(true)
    }, [])

    const predefinedDirectMessageRoom: Communication.DirectMessageRoom = useMemo(() => {
        return {
            roomId: Communication.PredefinedRoomId.DirectMessage,
            name: "Direct Message",
            type: Communication.RoomTypes.DirectChat, // Not DirectMessage type to show send options
            directMessageUserIds: [userId],
            isGroup: false
        }
    }, [userId])

    const {data: userRooms, isFetched} = useQuery<Communication.UserDirectAndDepartmentRooms>(
        ["user-department-room", userId, departmentId, enabledDirectMessages],
        async () => {
            setIsLoading(true)
            const studentCampusIds: number[] = user?.campusIds || []
            const currentUser = await model.getOneCompleteUser(model.user.id)
            const userCampuses = currentUser.profiles.map(({campuses}) => campuses.map(({id}) => id))
            const userCampusIds = userCampuses.flatMap((id) => id)
            const sameCampusIds = intersection(userCampusIds, studentCampusIds)

            // use student campus if no overlapped campus between current vs student
            const campusId = sameCampusIds.length ? sameCampusIds[0] : undefined
            const _rooms = await chatServiceV2.getUserRooms(departmentId, campusId || 0, userId)

            let {directRoom, departmentRoom, allowCreateDepartmentChat} = _rooms

            if (departmentRoom) {
                const {unread} = departmentRoom
                departmentRoom = await chatServiceV2.getRoom({roomId: departmentRoom.roomId})
                departmentRoom.unread = unread || 0
            }

            if (directRoom) {
                const {unread} = directRoom
                directRoom = await chatServiceV2.getRoom({roomId: directRoom.roomId})
                directRoom = convertRoom(Communication.RoomTypes.DirectChat, directRoom)
                directRoom.unread = unread || 0
            }

            const preferredKey =
                chatSettings?.defaultChatOnModule === Communication.DefaultChatOnModule.Departmental
                    ? STUDENT_CHAT_KEYS.DEPARTMENT
                    : STUDENT_CHAT_KEYS.DIRECT
            const canOpenDepartment = !!departmentRoom || allowCreateDepartmentChat
            const canOpenDirect = enabledDirectMessages

            const getDepartmentTargetRoom = (): Communication.DepartmentRoom | null => {
                if (departmentRoom) return departmentRoom
                if (allowCreateDepartmentChat) {
                    return {
                        roomId: Communication.PredefinedRoomId.WithoutDepartmentalRoom,
                        name: "Departmental Message",
                        type: Communication.RoomTypes.Department,
                        departmentId,
                        campusId,
                        user: {userId, fullName: getFullName(user)}
                    }
                }
                return null
            }

            const getDirectTargetRoom = (): Communication.DirectChatRoom | Communication.DirectMessageRoom | null => {
                if (!canOpenDirect) return null
                return directRoom || predefinedDirectMessageRoom
            }

            const fallbackKey =
                preferredKey === STUDENT_CHAT_KEYS.DEPARTMENT ? STUDENT_CHAT_KEYS.DIRECT : STUDENT_CHAT_KEYS.DEPARTMENT
            const selectedKey = (() => {
                if (preferredKey === STUDENT_CHAT_KEYS.DEPARTMENT && canOpenDepartment)
                    return STUDENT_CHAT_KEYS.DEPARTMENT
                if (preferredKey === STUDENT_CHAT_KEYS.DIRECT && canOpenDirect) return STUDENT_CHAT_KEYS.DIRECT
                if (fallbackKey === STUDENT_CHAT_KEYS.DEPARTMENT && canOpenDepartment)
                    return STUDENT_CHAT_KEYS.DEPARTMENT
                if (fallbackKey === STUDENT_CHAT_KEYS.DIRECT && canOpenDirect) return STUDENT_CHAT_KEYS.DIRECT
                return null
            })()

            if (selectedKey === STUDENT_CHAT_KEYS.DEPARTMENT) {
                setActiveKey(STUDENT_CHAT_KEYS.DEPARTMENT)
                const departmentTargetRoom = getDepartmentTargetRoom()
                if (departmentTargetRoom) changeRoom(departmentTargetRoom)
            } else if (selectedKey === STUDENT_CHAT_KEYS.DIRECT) {
                setActiveKey(STUDENT_CHAT_KEYS.DIRECT)
                const directTargetRoom = getDirectTargetRoom()
                if (directTargetRoom) {
                    if (preferredKey === STUDENT_CHAT_KEYS.DEPARTMENT && !canOpenDepartment) {
                        toastInfo(
                            "You haven't permission to access departmental chat with this student.\nYou can only send direct message to this student."
                        )
                    }
                    changeRoom(directTargetRoom)
                }
            } else {
                toastInfo("You haven't permission to access chat with this student")
            }
            setIsLoading(false)
            return {directRoom, departmentRoom, departmentId, campusId, userId, allowCreateDepartmentChat}
        },
        {
            initialData: {departmentId, userId, allowCreateDepartmentChat: false},
            enabled: !!user && !!userId && !!departmentId && isViewAsCleared,
            onError: (error) => {
                handleError(error)
            },
            refetchOnMount: true
        }
    )

    const {directRoom, departmentRoom, campusId, allowCreateDepartmentChat} = userRooms

    const onChangeTab = useCallback(
        (_activeKey: string) => {
            if (isFetched) {
                setActiveKey(_activeKey)
                if (_activeKey === STUDENT_CHAT_KEYS.DIRECT) {
                    if (directRoom && activeRoom.roomId !== directRoom.roomId) changeRoom(directRoom)
                    else {
                        changeRoom(predefinedDirectMessageRoom)
                    }
                } else {
                    if (departmentRoom && activeRoom.roomId !== departmentRoom.roomId) changeRoom(departmentRoom)
                    else {
                        const newRoom: Communication.DepartmentRoom = {
                            roomId: Communication.PredefinedRoomId.WithoutDepartmentalRoom,
                            name: "Departmental Message",
                            type: Communication.RoomTypes.Department,
                            departmentId,
                            campusId,
                            user: {userId, fullName: getFullName(user)}
                        }
                        changeRoom(newRoom)
                    }
                }
            }
        },
        [isFetched, directRoom, departmentRoom, activeRoom]
    )

    useEffect(() => {
        if (!enabledDirectMessages) {
            setActiveKey(STUDENT_CHAT_KEYS.DEPARTMENT)
        }
    }, [enabledDirectMessages])

    const isOnlyMessageCreation = useMemo(() => {
        return (
            (activeKey === STUDENT_CHAT_KEYS.DIRECT && !directRoom) ||
            (activeKey === STUDENT_CHAT_KEYS.DEPARTMENT && !departmentRoom)
        )
    }, [directRoom, departmentRoom, activeKey])

    if (!user) return null

    return (
        <div>
            <Tabs
                className="fullwidth klassSubTab"
                activeKey={activeKey}
                onChange={onChangeTab}
                tabBarStyle={isSidebarChat ? {margin: "6px 6px 2px 6px"} : {}}>
                {enabledDirectMessages && (
                    <Tabs.TabPane
                        className={cx(styles.tabContainer, {
                            [styles.reduceHeight]: isOnlyMessageCreation,
                            [styles.sidebarContainer]: isSidebarChat
                        })}
                        key={STUDENT_CHAT_KEYS.DIRECT}
                        tab={
                            <TabHeader
                                title="Direct"
                                indicator={
                                    directRoom?.unread > 0 ? (
                                        <span className={styles.unreadCount}>
                                            {getUnreadCountBadge(directRoom.unread)}
                                        </span>
                                    ) : undefined
                                }
                            />
                        }>
                        {activeRoom.roomId === directRoom?.roomId ? (
                            <DirectChat hideHeading />
                        ) : (
                            <div
                                className={cx(styles.composeContainer, {
                                    [styles.sidebarComposeContainer]: isSidebarChat
                                })}>
                                <span className={styles.noMessage}>No messages found. Let's start a chat!</span>
                                <Compose
                                    onSuccess={() => {
                                        toastSuccess("Message sent")
                                    }}
                                    emojiPickerClassName={styles.emojiPicker}
                                    userList={[{...user, userId}]}
                                    directParticipantInfo={user}
                                />
                            </div>
                        )}
                    </Tabs.TabPane>
                )}
                <Tabs.TabPane
                    disabled={!allowCreateDepartmentChat}
                    className={cx(styles.tabContainer, {
                        [styles.reduceHeight]: isOnlyMessageCreation,
                        [styles.sidebarContainer]: isSidebarChat
                    })}
                    key={STUDENT_CHAT_KEYS.DEPARTMENT}
                    tab={
                        <TabHeader
                            title="Department"
                            indicator={
                                departmentRoom?.unread > 0 ? (
                                    <span className={styles.unreadCount}>
                                        {getUnreadCountBadge(departmentRoom.unread)}
                                    </span>
                                ) : undefined
                            }
                        />
                    }>
                    {activeRoom.roomId === departmentRoom?.roomId ? (
                        <DirectChat hideHeading />
                    ) : (
                        <div className={cx(styles.composeContainer, {[styles.sidebarComposeContainer]: isSidebarChat})}>
                            <span className={styles.noMessage}>No messages found. Let's start a chat!</span>
                            <Compose
                                onSuccess={() => {
                                    toastSuccess("Message sent")
                                }}
                                emojiPickerClassName={styles.emojiPicker}
                                userList={[{...user, userId, isDepartmentChatUser: true}]}
                                directParticipantInfo={user}
                            />
                        </div>
                    )}
                </Tabs.TabPane>
            </Tabs>
            <BaseLoading isShow={isLoading || !isFetched} />
        </div>
    )
}
